/**
 * Guest-facing copy for the concierge chat, in the two supported UI languages.
 *
 * Voice (PRODUCT.md): a good host. Warm, brief, plain, second person; says what happened and what
 * to do next, then stops. No exclamation marks, no marketing lines, no "please" as padding.
 */

export type ChatLanguage = 'en' | 'el';

export const SUPPORTED_LANGUAGES: ChatLanguage[] = ['en', 'el'];

/** Reduces a BCP 47 tag ("el-GR", "EN_us") to a supported primary tag, or undefined. */
export function normalizeLanguage(value?: string | null): ChatLanguage | undefined {
   if (!value || typeof value !== 'string') return undefined;
   const primary = value.trim().toLowerCase().split(/[-_]/)[0];
   return SUPPORTED_LANGUAGES.find((l) => l === primary);
}

/** Notices the sign-in card can carry (resolved to text at render time so a language switch updates them). */
export type SignInNoticeKey = 'sessionExpired' | 'switchedHotel';

export interface ChatStrings {
   /** Header */
   conciergeTitle: string;
   byXenioAI: string;
   headerSub: string;
   aiDisclosure: string;
   privacy: string;
   languageCode: string;
   languageToggleLabel: (target: ChatLanguage) => string;
   themeToggleLabel: (target: 'light' | 'dark') => string;
   logoAlt: string;
   closeChat: string;

   /** Empty state */
   greeting: (hotelName?: string) => string;
   greetingHint: string;
   suggestionsLabel: string;

   /** Composer */
   placeholder: string;
   send: string;
   reconnecting: string;
   offline: string;

   /** Speaking a question */
   speak: string;
   voiceCancel: string;
   voiceAllow: string;
   voiceListening: string;
   voiceWriting: string;
   voiceNothing: string;
   voiceBlocked: string;
   voiceNoMic: string;
   voiceFailed: string;
   voiceTooMany: string;
   voiceUnavailable: string;
   voiceSent: (text: string) => string;

   /** Question line states */
   sending: string;
   notSent: string;
   retry: string;
   conflict: string;

   /** Answer */
   waiting: string;
   /** Under an answer that is still being written. */
   writing: string;
   providerError: string;
   sources: (count: number) => string;
   receptionTitle: string;
   receptionFallback: string;
   call: string;
   email: string;

   /** Photos beside an answer */
   photos: string;
   photoSlide: (position: number, total: number, title: string) => string;
   previousPhoto: string;
   nextPhoto: string;
   enlargePhoto: string;
   closePhoto: string;
   photoMissing: string;

   /** Banners */
   sendFailedBanner: string;
   conflictBanner: string;
   signInFailed: string;

   /** Notices */
   invalidLink: string;
   sessionExpired: string;
   switchedHotel: string;

   /** Front door */
   signInTitle: (hotelName?: string) => string;
   signInBody: string;
   signInUnavailable: string;
   signingIn: string;

   /** Verification dialog */
   verifyTitle: string;
   verifyBody: string;
   verifyName: string;
   verifyNamePlaceholder: string;
   verifyRoom: string;
   verifyRoomPlaceholder: string;
   verifyCancel: string;
   verifyConfirm: string;
   verifyBusy: string;
   verifyFoot: string;
   verifyFailed: string;
}

const en: ChatStrings = {
   conciergeTitle: 'Concierge',
   byXenioAI: 'by XenioAI',
   headerSub: 'Concierge · by XenioAI',
   aiDisclosure: "You're chatting with an AI concierge. For anything urgent, contact reception.",
   privacy: 'Privacy',
   languageCode: 'EN',
   languageToggleLabel: (target) => (target === 'el' ? 'Switch to Greek' : 'Switch to English'),
   themeToggleLabel: (target) => (target === 'dark' ? 'Switch to dark' : 'Switch to light'),
   logoAlt: 'XenioAI',
   closeChat: 'Close chat',

   greeting: (hotelName) => (hotelName ? `Ask anything about ${hotelName}.` : 'Ask anything about the hotel.'),
   greetingHint: "Whatever you'd ask at reception.",
   suggestionsLabel: 'Try one of these',

   placeholder: 'Ask about the hotel…',
   send: 'Send',
   reconnecting: 'Reconnecting…',
   offline: 'Not connected. Check your connection and try again.',

   speak: 'Speak your question',
   voiceCancel: 'Cancel',
   voiceAllow: 'Allow the microphone when your browser asks.',
   voiceListening: 'Listening. It sends when you stop talking.',
   voiceWriting: 'Writing down what you said…',
   voiceNothing: 'Nothing came through. Try again, a little closer to the microphone.',
   voiceBlocked: "The microphone is blocked. Allow it in your browser's settings, or type your question.",
   voiceNoMic: 'No microphone was found. Type your question instead.',
   voiceFailed: "That didn't come through. Try again, or type your question.",
   voiceTooMany: 'Too many in a row. Wait a moment, or type your question.',
   voiceUnavailable: "Speaking isn't available right now. Type your question instead.",
   voiceSent: (text) => `Sent: ${text}`,

   sending: 'Sending…',
   notSent: "This didn't send.",
   retry: 'Try again',
   conflict: "This can't be re-sent. Ask it again as a new message.",

   waiting: "Looking through the hotel's pages…",
   writing: 'Still writing…',
   providerError: "The concierge can't be reached right now.",
   sources: (count) => (count === 1 ? 'From 1 hotel source' : `From ${count} hotel sources`),
   receptionTitle: 'Reception',
   receptionFallback: 'Ask at the front desk.',
   call: 'Call',
   email: 'Email',

   photos: 'Photos',
   photoSlide: (position, total, title) => `${title}, ${position} of ${total}`,
   previousPhoto: 'Previous photo',
   nextPhoto: 'Next photo',
   enlargePhoto: 'View larger',
   closePhoto: 'Close photo',
   photoMissing: 'Photo not available',

   sendFailedBanner: "This didn't send. Try again.",
   conflictBanner: "This can't be re-sent. Ask it again as a new message.",
   signInFailed: "Sign-in didn't work. Try again.",

   invalidLink: "This chat link isn't valid. Open the concierge from your hotel's link or QR code.",
   sessionExpired: 'Your session ended. Sign in again to pick up where you left off.',
   switchedHotel: 'This link is for a different hotel. Sign in again to continue.',

   signInTitle: (hotelName) => (hotelName ? `Ask ${hotelName} anything.` : 'Ask the hotel anything.'),
   signInBody: 'Sign in with Google to start. Your conversation stays yours.',
   signInUnavailable: "Sign-in isn't available right now. Try again in a moment, or ask reception.",
   signingIn: 'Signing in',

   verifyTitle: 'Confirm your stay',
   verifyBody: 'Your name and room number, as on your booking.',
   verifyName: 'Name on the booking',
   verifyNamePlaceholder: 'As written on your booking',
   verifyRoom: 'Room number',
   verifyRoomPlaceholder: 'For example 204',
   verifyCancel: 'Not now',
   verifyConfirm: 'Confirm',
   verifyBusy: 'Confirming…',
   verifyFoot: "Checked against the hotel's records.",
   verifyFailed: "That didn't go through. Try again."
};

const el: ChatStrings = {
   conciergeTitle: 'Κοντσιέρζ',
   byXenioAI: 'από την XenioAI',
   headerSub: 'Κοντσιέρζ · από την XenioAI',
   aiDisclosure: 'Συνομιλείτε με έναν κοντσιέρζ τεχνητής νοημοσύνης. Για κάτι επείγον, επικοινωνήστε με τη ρεσεψιόν.',
   privacy: 'Απόρρητο',
   languageCode: 'EL',
   languageToggleLabel: (target) => (target === 'el' ? 'Αλλαγή σε Ελληνικά' : 'Αλλαγή σε Αγγλικά'),
   themeToggleLabel: (target) => (target === 'dark' ? 'Σκούρο θέμα' : 'Φωτεινό θέμα'),
   logoAlt: 'XenioAI',
   closeChat: 'Κλείσιμο συνομιλίας',

   greeting: () => 'Ρωτήστε ό,τι θέλετε για το ξενοδοχείο.',
   greetingHint: 'Ό,τι θα ρωτούσατε στη ρεσεψιόν.',
   suggestionsLabel: 'Δοκιμάστε μία από αυτές',

   placeholder: 'Ρωτήστε για το ξενοδοχείο…',
   send: 'Αποστολή',
   reconnecting: 'Επανασύνδεση…',
   offline: 'Χωρίς σύνδεση. Ελέγξτε τη σύνδεσή σας και δοκιμάστε ξανά.',

   speak: 'Πείτε την ερώτησή σας',
   voiceCancel: 'Ακύρωση',
   voiceAllow: 'Επιτρέψτε το μικρόφωνο όταν σας το ζητήσει το πρόγραμμα περιήγησης.',
   voiceListening: 'Σας ακούμε. Στέλνεται μόλις σταματήσετε να μιλάτε.',
   voiceWriting: 'Καταγράφουμε ό,τι είπατε…',
   voiceNothing: 'Δεν ακούστηκε τίποτα. Δοκιμάστε ξανά, λίγο πιο κοντά στο μικρόφωνο.',
   voiceBlocked: 'Το μικρόφωνο είναι μπλοκαρισμένο. Επιτρέψτε το στις ρυθμίσεις του προγράμματος περιήγησης ή γράψτε την ερώτησή σας.',
   voiceNoMic: 'Δεν βρέθηκε μικρόφωνο. Γράψτε την ερώτησή σας.',
   voiceFailed: 'Δεν ολοκληρώθηκε. Δοκιμάστε ξανά ή γράψτε την ερώτησή σας.',
   voiceTooMany: 'Πάρα πολλά στη σειρά. Περιμένετε λίγο ή γράψτε την ερώτησή σας.',
   voiceUnavailable: 'Η ομιλία δεν είναι διαθέσιμη αυτή τη στιγμή. Γράψτε την ερώτησή σας.',
   voiceSent: (text) => `Στάλθηκε: ${text}`,

   sending: 'Στέλνεται…',
   notSent: 'Δεν στάλθηκε.',
   retry: 'Δοκιμάστε ξανά',
   conflict: 'Δεν μπορεί να ξανασταλεί. Ρωτήστε το ξανά ως νέο μήνυμα.',

   waiting: 'Ψάχνουμε στις σελίδες του ξενοδοχείου…',
   writing: 'Γράφουμε ακόμη…',
   providerError: 'Ο κοντσιέρζ δεν είναι διαθέσιμος αυτή τη στιγμή.',
   sources: (count) => (count === 1 ? 'Από 1 πηγή του ξενοδοχείου' : `Από ${count} πηγές του ξενοδοχείου`),
   receptionTitle: 'Ρεσεψιόν',
   receptionFallback: 'Ρωτήστε στη ρεσεψιόν.',
   call: 'Κλήση',
   email: 'Email',

   photos: 'Φωτογραφίες',
   photoSlide: (position, total, title) => `${title}, ${position} από ${total}`,
   previousPhoto: 'Προηγούμενη φωτογραφία',
   nextPhoto: 'Επόμενη φωτογραφία',
   enlargePhoto: 'Προβολή σε μεγαλύτερο μέγεθος',
   closePhoto: 'Κλείσιμο φωτογραφίας',
   photoMissing: 'Η φωτογραφία δεν είναι διαθέσιμη',

   sendFailedBanner: 'Δεν στάλθηκε. Δοκιμάστε ξανά.',
   conflictBanner: 'Δεν μπορεί να ξανασταλεί. Ρωτήστε το ξανά ως νέο μήνυμα.',
   signInFailed: 'Η σύνδεση δεν ολοκληρώθηκε. Δοκιμάστε ξανά.',

   invalidLink: 'Αυτός ο σύνδεσμος δεν ισχύει. Ανοίξτε τον κοντσιέρζ από τον σύνδεσμο ή τον κωδικό QR του ξενοδοχείου σας.',
   sessionExpired: 'Η συνεδρία σας έληξε. Συνδεθείτε ξανά για να συνεχίσετε από εκεί που σταματήσατε.',
   switchedHotel: 'Αυτός ο σύνδεσμος είναι για άλλο ξενοδοχείο. Συνδεθείτε ξανά για να συνεχίσετε.',

   signInTitle: () => 'Ρωτήστε το ξενοδοχείο ό,τι θέλετε.',
   signInBody: 'Συνδεθείτε με Google για να ξεκινήσετε. Η συνομιλία σας μένει δική σας.',
   signInUnavailable: 'Η σύνδεση δεν είναι διαθέσιμη αυτή τη στιγμή. Δοκιμάστε ξανά σε λίγο ή ρωτήστε στη ρεσεψιόν.',
   signingIn: 'Γίνεται σύνδεση',

   verifyTitle: 'Επιβεβαιώστε τη διαμονή σας',
   verifyBody: 'Το όνομα και ο αριθμός δωματίου, όπως στην κράτησή σας.',
   verifyName: 'Όνομα κράτησης',
   verifyNamePlaceholder: 'Όπως αναγράφεται στην κράτηση',
   verifyRoom: 'Αριθμός δωματίου',
   verifyRoomPlaceholder: 'Για παράδειγμα 204',
   verifyCancel: 'Όχι τώρα',
   verifyConfirm: 'Επιβεβαίωση',
   verifyBusy: 'Επιβεβαίωση…',
   verifyFoot: 'Ελέγχεται με τα στοιχεία του ξενοδοχείου.',
   verifyFailed: 'Δεν ολοκληρώθηκε. Δοκιμάστε ξανά.'
};

const STRINGS: Record<ChatLanguage, ChatStrings> = { en, el };

export function getChatStrings(language: ChatLanguage): ChatStrings {
   return STRINGS[language] ?? en;
}
